import { UserToggles } from '@ravago/shared/page-data/models/config';

import { RfqStep } from '../models/request-for-quotation-step';

import { usePreviousValue } from './use-previous-value';

interface Props {
   initialStep?: RfqStep;
   userToggles: UserToggles;
}

const VERIFIED_FLOW = [RfqStep.QUOTATION_DETAILS, RfqStep.ADDRESS_DETAILS, RfqStep.SUMMARY, RfqStep.FINAL_MESSAGE];
const GUEST_OR_PENDING_FLOW = [RfqStep.QUOTATION_DETAILS, RfqStep.CONTACT_DETAILS, RfqStep.DELIVERY_AREA, RfqStep.SUMMARY, RfqStep.FINAL_MESSAGE];
const ANONYMOUS_FLOW = [
   RfqStep.QUOTATION_DETAILS,
   RfqStep.VERIFY_EMAIL,
   RfqStep.CONTACT_DETAILS,
   RfqStep.DELIVERY_AREA,
   RfqStep.SUMMARY,
   RfqStep.FINAL_MESSAGE
];

const getStepFlow = (userToggles: UserToggles) => {
   if (userToggles.hasVerifiedToggle) {
      return VERIFIED_FLOW;
   }
   if (userToggles.hasGuestToggle || userToggles.hasPendingToggle) {
      return GUEST_OR_PENDING_FLOW;
   }
   return ANONYMOUS_FLOW;
};

export const useRequestForQuotationSteps = ({ userToggles, initialStep }: Props) => {
   const steps = getStepFlow(userToggles);

   const previousStep = () => {
      const currentIndex = steps.indexOf(initialStep ?? steps[0]);
      return currentIndex > 0 ? steps[currentIndex - 1] : steps[0];
   };

   const [currentStep, setCurrentStep, revertCurrentStep, reset] = usePreviousValue(initialStep ?? steps[0], previousStep);

   const currentStepIndex = currentStep === RfqStep.RECOGNIZED_EMAIL ? 1 : steps.indexOf(currentStep);

   const nextStep = (context?: { userExists: boolean }) => {
      if (currentStep === RfqStep.VERIFY_EMAIL && context) {
         if (context.userExists) {
            setCurrentStep(RfqStep.RECOGNIZED_EMAIL); // Optional, if it's shown
         } else {
            setCurrentStep(RfqStep.CONTACT_DETAILS);
         }
         return;
      }

      if (currentStep === RfqStep.RECOGNIZED_EMAIL) {
         return;
      }

      const next = steps[currentStepIndex + 1];
      if (next) setCurrentStep(next);
   };

   return {
      currentStep,
      setCurrentStep,
      currentStepIndex,
      nextStep,
      previousStep: revertCurrentStep,
      reset,
      stepCount: steps.length - 1 // Don't include the last step
   };
};
